import { useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight, FlaskConical, X } from 'lucide-react';


/**
 * Thin ribbon shown at the very top of every surface in demo mode.
 * Dismissible per session via sessionStorage.
 *
 * @param {('default'|'portal'|'platform')} variant  Controls colour scheme.
 */
export const DEMO_TENANT_ID = 'tenant_demo_workspace';

/** Shown only while someone is inside the demo pharmacy. */
export function DemoBanner({ variant = 'default', show = false, onSignup }) {
  const ribbonRef = useRef(null);
  const isDemoMode = show;
  const [leaving, setLeaving] = useState(false);
  const [hidden, setHidden] = useState(() => {
    if (onSignup) return false;
    try {
      return sessionStorage.getItem('demo_banner_hidden') === '1';
    } catch {
      return false;
    }
  });
  useLayoutEffect(() => {
    if (!isDemoMode || !ribbonRef.current || hidden) return;
    const update = () => document.documentElement.style.setProperty('--demo-ribbon-height', `${ribbonRef.current?.getBoundingClientRect().height || 0}px`);
    const observer = new ResizeObserver(update);
    observer.observe(ribbonRef.current);
    update();
    return () => observer.disconnect();
  }, [hidden, isDemoMode]);
  if (!isDemoMode || hidden) return null;

  const dismiss = () => {
    try { sessionStorage.setItem('demo_banner_hidden', '1'); } catch { /* */ }
    setHidden(true);
  };

  const colours = {
    default: { bg: '#FFFBEB', border: '#FDE68A', text: '#B45309' },
    portal: { bg: '#F0FDF4', border: '#BBF7D0', text: '#15803D' },
    platform: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8' },
  };
  const c = colours[variant] || colours.default;

  return (
    <div
      className="demo-env-ribbon"
      ref={ribbonRef}
      style={{ background: c.bg, borderBottom: `1px solid ${c.border}`, color: c.text }}
      data-testid="demo-environment-banner"
      role="status"
    >
      <FlaskConical className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="flex-1 text-center">Demo pharmacy · sample data only · No real pharmacy or patient data</span>
      {onSignup ? (
        <button
          type="button"
          disabled={leaving}
          onClick={async () => { setLeaving(true); try { await onSignup(); } finally { setLeaving(false); } }}
          data-testid="demo-start-own-pharmacy-btn"
          className="demo-env-ribbon-cta"
        >
          {leaving ? 'Leaving demo…' : 'Start my own pharmacy'}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </button>
      ) : (
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss demo notice"
        data-testid="demo-banner-dismiss"
        className="demo-env-ribbon-x"
      >
        <X className="h-3 w-3" />
      </button>
      )}
    </div>
  );
}
